if (!customElements.get('price-with-discount')) {
  class PriceWithDiscount extends HTMLElement {
    constructor() {
      super();
      this.variantId = null;
      this.productTiers = null;
      this.variantTiers = null;
      this.ignoreVariantsMetafields = false;
      this.currentVariantPrice = null;
      this.currentVariantCompareAtPrice = null;
      this.productCompareAtPrice = null;
      this.variantSelector = null;
      this.productCard = null;
      this.variantObserver = null;
      this.boundHandleVariantChange = null;
      // Product context tracking for filtering events
      this.productHandle = null;
      this.formId = null;
      this.productOptions = null;
    }

    connectedCallback() {
      this.initialize();

      // Skip JS updates for static product cards (pre-rendered, don't need updates)
      if (this.productCard && this.productCard.hasAttribute('data-static')) {
        return;
      }

      this.setupEventListeners();
      this.updatePrice();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      // Check if we're inside a product-card element
      this.productCard = this.closest('product-card');
      
      // Get product context for filtering events (only if not in product-card)
      if (!this.productCard) {
        // Find product-options component to get product context
        this.productOptions = this.closest('product-options');
        if (this.productOptions) {
          this.productHandle = this.productOptions.productHandle || this.productOptions.getAttribute('data-product-handle');
        }
        
        // Get form ID for context matching
        const form = this.closest('form[action*="/cart/add"]');
        if (form) {
          this.formId = form.id || null;
        }
        if (!this.formId && this.productOptions) {
          this.formId = this.productOptions.getAttribute('data-form-id') || null;
        }
      }
      
      // Get initial variant ID based on context
      // CRITICAL: Always prioritize product-card's data-variant-id if inside one
      this.updateCurrentVariantId();

      // Get product tiers from data attribute
      const productTiersData = this.getAttribute('data-product-tiers');
      if (productTiersData) {
        try {
          const parsed = JSON.parse(productTiersData);
          this.productTiers = Array.isArray(parsed.tiers) ? parsed.tiers : [];
          this.ignoreVariantsMetafields = parsed.ignoreVariantsMetafields || false;
        } catch (e) {
          this.productTiers = [];
          this.ignoreVariantsMetafields = false;
        }
      }

      // Get variant tiers from data attribute
      const variantTiersData = this.getAttribute('data-variant-tiers');
      if (variantTiersData) {
        try {
          const parsed = JSON.parse(variantTiersData);
          this.variantTiers = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          this.variantTiers = [];
        }
      }

      // Get product-level compare_at_price from data attribute (fallback)
      const comparePriceData = this.getAttribute('data-compare-price');
      if (comparePriceData) {
        const comparePrice = parseInt(comparePriceData, 10);
        if (comparePrice > 0) {
          this.productCompareAtPrice = comparePrice;
        }
      }

      // Set placeholder content (product.price)
      if (!this.textContent.trim()) {
        const placeholderPrice = this.getAttribute('data-placeholder-price');
        if (placeholderPrice) {
          this.textContent = placeholderPrice;
        }
      }
    }

    updateCurrentVariantId() {
      // CRITICAL: Always prioritize product-card's data-variant-id if inside one
      // Price components inside product-cards should ONLY use the product-card's variant ID
      if (this.productCard) {
        // Get variant ID from product-card's data-variant-id attribute
        const variantIdAttr = this.productCard.getAttribute('data-variant-id');
        if (variantIdAttr) {
          this.variantId = parseInt(variantIdAttr);
          return;
        }
      } else {
        // Not in product-card: try to get from product-options component
        if (this.productOptions && this.productOptions.variantSelector) {
          const variantSelector = this.productOptions.variantSelector;
          if (variantSelector && variantSelector.value) {
            this.variantId = parseInt(variantSelector.value);
            this.variantSelector = variantSelector;
            return;
          }
        }
        
        // Fallback: try to get from main product form
        const mainForm = document.getElementById('main-product-form');
        if (mainForm) {
          const variantSelector = mainForm.querySelector('select[name="id"]');
          if (variantSelector && variantSelector.value) {
            this.variantId = parseInt(variantSelector.value);
            this.variantSelector = variantSelector;
            return;
          }
        }
      }
      
      // Fallback: try data-variant attribute (legacy)
      const variantIdAttr = this.getAttribute('data-variant');
      if (variantIdAttr) {
        this.variantId = parseInt(variantIdAttr);
      }
    }

    setupEventListeners() {
      if (this.productCard) {
        // Setup listeners for product-card context
        // CRITICAL: Price components inside product-cards should ONLY use the product-card's data-variant-id
        // and should NOT listen to product-options:changed events to prevent cross-contamination
        
        // Watch for changes to data-variant-id on the product-card
        this.variantObserver = new MutationObserver(() => {
          const oldVariantId = this.variantId;
          this.updateCurrentVariantId();
          if (oldVariantId !== this.variantId) {
            this.updatePrice();
          }
        });

        // Observe the product-card for data-variant-id changes
        this.variantObserver.observe(this.productCard, {
          attributes: true,
          attributeFilter: ['data-variant-id']
        });

        // DO NOT listen to product-options:changed events when inside product-card
        // The MutationObserver above handles all variant changes via product-card's data-variant-id
      } else {
        // Setup listeners for product form context (main product page)
        this.boundHandleVariantChange = this.handleVariantChange.bind(this);
        
        // Listen for variant changes from main product form
        const mainForm = document.getElementById('main-product-form');
        if (mainForm) {
          const variantSelector = mainForm.querySelector('select[name="id"]');
          if (variantSelector) {
            this.variantSelector = variantSelector;
            variantSelector.addEventListener('change', this.boundHandleVariantChange);
          }
        }

        // Listen for custom variant change events, but filter by product context
        document.addEventListener('product-options:changed', this.boundHandleVariantChange);

        // Listen for URL changes (variant query param) - check on initial load
        this.checkUrlVariant();
        
        // Watch for URL changes (popstate)
        window.addEventListener('popstate', this.checkUrlVariant.bind(this));
      }
    }

    checkUrlVariant() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      if (variantId && parseInt(variantId) !== this.variantId) {
        this.variantId = parseInt(variantId);
        this.updatePrice();
      }
    }

    handleVariantChange(event) {
      // CRITICAL: If inside product-card, ignore product-options:changed events
      // Price components inside product-cards should ONLY use product-card's data-variant-id
      if (this.productCard) {
        return; // Ignore event, rely on MutationObserver for product-card changes
      }
      
      // For non-product-card contexts, filter events by product context
      if (event && event.detail) {
        const { productHandle: eventProductHandle, sourceFormId: eventFormId, sourceId: eventSourceId } = event.detail;
        
        // Check if event is from a different product/form
        if (eventProductHandle && this.productHandle && eventProductHandle !== this.productHandle) {
          return; // Ignore events from different products
        }
        
        // Check if event is from a different form (if we have form context)
        if (eventFormId && this.formId && eventFormId !== this.formId) {
          return; // Ignore events from different forms
        }
        
        // Check if event is from the same product-options instance (if we have one)
        if (eventSourceId && this.productOptions && this.productOptions.id && eventSourceId !== this.productOptions.id) {
          return; // Ignore events from different product-options instances
        }
      }
      
      let newVariantId = null;
      
      // Get variant ID from the changed select element
      if (event && event.target && event.target.name === 'id') {
        newVariantId = parseInt(event.target.value);
      } else if (event && event.detail && event.detail.variantId) {
        // Handle custom events with variant ID in detail
        newVariantId = parseInt(event.detail.variantId);
      } else if (this.variantSelector) {
        // Fallback: get from stored variant selector reference
        newVariantId = parseInt(this.variantSelector.value);
      } else {
        // Last resort: try to get variant from URL
        this.checkUrlVariant();
        return;
      }
      
      if (newVariantId && newVariantId !== this.variantId) {
        this.variantId = newVariantId;
        this.updatePrice();
      }
    }

    getDiscountTiersForVariant(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      // If variant tiers exist and ignoreVariantsMetafields is false, use variant tiers
      if (this.variantTiers && Array.isArray(this.variantTiers) && !this.ignoreVariantsMetafields) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        
        // If variant has tiers property, use it (even if empty array) - variant takes priority
        if (variantData && variantData.tiers !== undefined) {
          // Handle nested tiers structure: { tiers: { tiers: [...] } }
          let tiersArray = null;
          if (Array.isArray(variantData.tiers)) {
            tiersArray = variantData.tiers;
          } else if (variantData.tiers && variantData.tiers.tiers && Array.isArray(variantData.tiers.tiers)) {
            tiersArray = variantData.tiers.tiers;
          }
          // Return tiers array (even if empty) - don't fall back to product
          return tiersArray || [];
        }
      }
      
      // Only fall back to product-level tiers if variant has no tiers property at all
      if (this.productTiers && Array.isArray(this.productTiers) && this.productTiers.length > 0) {
        return this.productTiers;
      }
      
      return [];
    }

    getVariantPrice(variantId) {
      
      if (this.variantTiers && Array.isArray(this.variantTiers)) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
          return vId === normalizedVariantId;
        });
        if (variantData && variantData.price) {
          // Price is already in cents from Liquid
          return variantData.price;
        }
      }
      
      // Fallback: try to get from data attribute or use placeholder
      const placeholderPrice = this.getAttribute('data-placeholder-price');
      if (placeholderPrice) {
        // Remove currency symbols and parse, then convert to cents
        const numericPrice = parseFloat(placeholderPrice.replace(/[^0-9.]/g, ''));
        return window.theme?.utils?.roundPrice ? 
          window.theme.utils.roundPrice(numericPrice * 100, true) : 
          Math.round(numericPrice * 100); // Convert to cents
      }
      
      return null;
    }

    getVariantCompareAtPrice(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      if (this.variantTiers && Array.isArray(this.variantTiers)) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        if (variantData && variantData.compare_at_price) {
          // compare_at_price is already in cents from Liquid
          const comparePrice = parseInt(variantData.compare_at_price, 10);
          if (comparePrice > 0) {
            return comparePrice;
          }
        }
      }
      
      // Fallback to product-level compare_at_price
      if (this.productCompareAtPrice && this.productCompareAtPrice > 0) {
        return this.productCompareAtPrice;
      }
      
      return null;
    }

    calculateSavingsPercentage(originalPrice, salePrice) {
      if (!originalPrice || !salePrice || originalPrice <= salePrice) {
        return 0;
      }
      const savings = originalPrice - salePrice;
      const percentage = (savings / originalPrice) * 100;
      return window.theme?.utils?.roundPrice ? 
        window.theme.utils.roundPrice(percentage, false) : 
        Math.round(percentage);
    }

    calculateDiscountedPrice(variantPrice, discountPercentage) {
      if (!variantPrice || !discountPercentage) {
        return null;
      }

      const discountDecimal = discountPercentage / 100.0;
      const discountAmount = variantPrice * discountDecimal;
      const discountedPrice = variantPrice - discountAmount;
      
      return window.theme?.utils?.roundPrice ? 
        window.theme.utils.roundPrice(discountedPrice, true) : 
        Math.round(discountedPrice); // Fallback to nearest cent
    }

    formatMoney(cents) {
      // Check if rate conversion should be applied (default: false)
      const applyRate = this.hasAttribute('data-apply-rate') && 
                       this.getAttribute('data-apply-rate') !== 'false';
      
      // Get currency info
      const currency = window.Shopify?.currency || { active: 'GBP', rate: '1.0' };
      const currencyCode = currency.active || 'GBP';
      
      // Convert cents to base amount
      const baseAmount = cents / 100;
      
      // Apply rate conversion only if enabled
      let amountToFormat = baseAmount;
      if (applyRate) {
        const rate = parseFloat(currency.rate || '1.0');
        amountToFormat = baseAmount * rate;
      }
      
      // Use existing utility function if available
      if (typeof window.theme?.utils?.money === 'function') {
        return window.theme.utils.money(amountToFormat);
      }
      
      // Fallback: format manually using Shopify currency
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amountToFormat);
    }

    updatePrice() {
      if (!this.variantId) {
        return;
      }

      const variantPrice = this.getVariantPrice(this.variantId);
      if (!variantPrice) {
        return;
      }

      this.currentVariantPrice = variantPrice;
      
      // Get compare_at_price for this variant (used as original price when available)
      const variantCompareAtPrice = this.getVariantCompareAtPrice(this.variantId);
      this.currentVariantCompareAtPrice = variantCompareAtPrice;
      
      const tiers = this.getDiscountTiersForVariant(this.variantId);
      
      // Get discount from first tier (matching justwear-product-card-discount-label.liquid logic)
      let discountPercentage = 0;
      if (tiers && tiers.length > 0) {
        const firstTier = tiers[0];
        discountPercentage = parseFloat(firstTier.discount_amount_value, 10) || 0;
      }

      // Determine the base price for discount calculation (always use variant price for tier discounts)
      const basePriceForDiscount = variantPrice;
      
      // Determine the "original price" to display - use compare_at_price if available and higher, otherwise use variant price
      const originalPriceCents = variantCompareAtPrice && variantCompareAtPrice > variantPrice 
        ? variantCompareAtPrice 
        : variantPrice;

      if (discountPercentage > 0) {
        // Discount tiers exist - apply discount to variant price
        const discountedPriceCents = this.calculateDiscountedPrice(basePriceForDiscount, discountPercentage);
        const discountedPriceFormatted = this.formatMoney(discountedPriceCents);
        
        // Show original price (compare_at_price if available, otherwise variant price)
        const originalPriceFormatted = this.formatMoney(originalPriceCents);

        // Only show crossed-out price if prices differ
        if (originalPriceCents !== discountedPriceCents) {
          // Update content with both prices
          this.innerHTML = `<span class="text-[#C90016]">${discountedPriceFormatted}</span> <span class="line-through">${originalPriceFormatted}</span>`;
        } else {
          // Prices are equal, show only one price
          this.textContent = discountedPriceFormatted;
        }
      } else if (variantCompareAtPrice && variantCompareAtPrice > variantPrice) {
        // No discount tiers, but compare_at_price exists - show compare_at_price as original
        const variantPriceFormatted = this.formatMoney(variantPrice);
        const originalPriceFormatted = this.formatMoney(variantCompareAtPrice);
        
        // Show both prices when compare_at_price exists
        this.innerHTML = `<span class="text-[#C90016]">${variantPriceFormatted}</span> <span class="line-through">${originalPriceFormatted}</span>`;
      } else {
        // No discount and no compare_at_price, just show the variant price
        const variantPriceFormatted = this.formatMoney(variantPrice);
        this.textContent = variantPriceFormatted;
      }
    }

    cleanup() {
      // Disconnect the variant observer if it exists
      if (this.variantObserver) {
        this.variantObserver.disconnect();
        this.variantObserver = null;
      }
      
      // Remove event listeners (only if they were added - not added for product-card context)
      if (this.boundHandleVariantChange && !this.productCard) {
        document.removeEventListener('product-options:changed', this.boundHandleVariantChange);
      }
      
      if (this.variantSelector && this.boundHandleVariantChange) {
        this.variantSelector.removeEventListener('change', this.boundHandleVariantChange);
      }
    }
  }

  customElements.define('price-with-discount', PriceWithDiscount);
}

